<template>
  <v-container fluid class="sp-page">
    <page-skeleton v-if="loading" variant="cards" class="mt-4" />

    <template v-else>
      <header class="sp-page__head">
        <p class="sp-page__eyebrow">{{ $t("studyPlan.eyebrow") }}</p>
        <h1 class="sp-page__title">{{ $t("studyPlan.title") }}</h1>
        <p class="sp-page__sub">{{ $t("studyPlan.subtitle") }}</p>
      </header>

      <p v-if="failed" class="sp-page__empty">
        {{ $t("studyPlan.loadError") }}
      </p>

      <div v-else-if="!current.length && !past.length" class="sp-page__empty">
        <h2 class="sp-page__empty-title">{{ $t("studyPlan.emptyTitle") }}</h2>
        <p>{{ $t("studyPlan.emptyText") }}</p>
      </div>

      <template v-else>
        <section v-if="current.length" class="sp-page__section">
          <h2 class="sp-page__label">{{ $t("studyPlan.current") }}</h2>
          <study-plan-trail
            v-for="plan in current"
            :key="plan.id"
            :plan="plan"
            class="sp-page__plan"
            @update="replaceCurrent"
          />
        </section>

        <p v-else class="sp-page__none">{{ $t("studyPlan.noCurrent") }}</p>

        <section v-if="past.length" class="sp-page__section">
          <h2 class="sp-page__label">{{ $t("studyPlan.past") }}</h2>

          <div v-for="plan in past" :key="plan.id" class="sp-past">
            <button
              type="button"
              class="sp-past__row"
              :aria-expanded="openId === plan.id"
              @click="togglePast(plan)"
            >
              <span class="sp-past__name">{{ plan.name }}</span>
              <span class="sp-past__meta">
                {{ pastStatus(plan) }} ·
                {{
                  $t("studyPlan.deadlineUntil", {
                    date: formatDay(plan.deadline),
                  })
                }}
              </span>
              <v-icon size="20" class="sp-past__chev">{{
                openId === plan.id ? "mdi-chevron-up" : "mdi-chevron-down"
              }}</v-icon>
            </button>

            <div v-if="openId === plan.id" class="sp-past__body">
              <v-progress-linear
                v-if="!details[plan.id]"
                indeterminate
                color="#a826e1"
              />
              <study-plan-trail
                v-else
                :plan="details[plan.id]"
                readonly
                compact
              />
            </div>
          </div>
        </section>
      </template>
    </template>
  </v-container>
</template>

<script>
import pitchCustomerApi from "@/shared/pitchCustomerApi";
import StudyPlanTrail from "@/components/studyPlan/StudyPlanTrail.vue";

// Plano de estudos do aluno autônomo (card 86akj8q08): o plano vigente com as
// etapas e os planos anteriores para consulta.
export default {
  name: "StudyPlanPage",

  components: { StudyPlanTrail },

  data() {
    return {
      loading: true,
      failed: false,
      current: [],
      past: [],
      openId: "",
      details: {},
    };
  },

  mounted() {
    this.load();
  },

  methods: {
    formatDay(value) {
      return String(value || "")
        .slice(0, 10)
        .split("-")
        .reverse()
        .join("/");
    },

    load() {
      pitchCustomerApi
        .getStudyPlans()
        .then((res) => {
          this.current = res.data?.data?.current || [];
          this.past = res.data?.data?.past || [];
        })
        .catch(() => {
          this.failed = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // O servidor devolve o plano inteiro depois de cada ação. Se ele acabou de
    // ser concluído, passa para os anteriores.
    replaceCurrent(plan) {
      if (plan.phase !== "active") {
        this.current = this.current.filter((item) => item.id !== plan.id);
        this.past = [plan, ...this.past.filter((item) => item.id !== plan.id)];
        this.$set(this.details, plan.id, plan);
        return;
      }
      this.current = this.current.map((item) =>
        item.id === plan.id ? plan : item
      );
    },

    pastStatus(plan) {
      if (plan.completedAt) {
        return plan.completedOnTime
          ? this.$t("studyPlan.doneOnTime")
          : this.$t("studyPlan.doneLate");
      }
      return this.$t("studyPlan.expired");
    },

    togglePast(plan) {
      this.openId = this.openId === plan.id ? "" : plan.id;
      if (this.openId && !this.details[plan.id]) {
        pitchCustomerApi
          .getStudyPlan(plan.id)
          .then((res) => this.$set(this.details, plan.id, res.data.data))
          .catch(() => {
            this.openId = "";
            this.$root.$emit("display-alert", {
              text: this.$t("studyPlan.loadError"),
              type: 1,
            });
          });
      }
    },
  },
};
</script>

<style scoped>
.sp-page {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --line: var(--dk-line, #ece6ef);
  --surface: var(--dk-surface, #fff);
  --paper: var(--dk-paper, #fbf7fa);
  --grape: var(--dk-grape, #a826e1);
  max-width: 880px;
  margin: 0 auto;
  padding: 24px 12px 56px;
  font-family: "Poppins", sans-serif;
  color: var(--ink);
}

.sp-page__eyebrow {
  margin: 0 0 8px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--grape);
}

.sp-page__title {
  margin: 0;
  font-size: clamp(1.6rem, 4vw, 2.1rem);
  font-weight: 700;
  line-height: 1.2;
}

.sp-page__sub {
  margin: 8px 0 28px;
  max-width: 60ch;
  font-size: 0.9rem;
  color: var(--muted);
}

.sp-page__section {
  margin-bottom: 36px;
}

.sp-page__label {
  margin: 0 0 14px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted);
}

.sp-page__plan + .sp-page__plan {
  margin-top: 36px;
}

.sp-page__empty {
  padding: 32px 24px;
  border: 1px dashed var(--line);
  border-radius: 16px;
  background: var(--paper);
  font-size: 0.9rem;
  color: var(--muted);
}

.sp-page__empty-title {
  margin: 0 0 6px;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--ink);
}

.sp-page__none {
  margin: 0 0 32px;
  font-size: 0.9rem;
  color: var(--muted);
}

/* ---------- planos anteriores ---------- */
.sp-past {
  margin-bottom: 10px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--surface);
}

.sp-past__row {
  display: flex;
  align-items: center;
  gap: 4px 16px;
  flex-wrap: wrap;
  width: 100%;
  padding: 14px 18px;
  border: 0;
  background: transparent;
  color: inherit;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.sp-past__row:focus-visible {
  outline: 3px solid rgba(168, 38, 225, 0.35);
  outline-offset: -3px;
  border-radius: 14px;
}

.sp-past__name {
  flex: 1 1 200px;
  font-size: 0.95rem;
  font-weight: 600;
}

.sp-past__meta {
  font-size: 0.8rem;
  color: var(--muted);
}

.sp-past__chev {
  color: var(--muted) !important;
}

.sp-past__body {
  padding: 4px 18px 18px;
}
</style>
